// Finance > Commissions > Settings (2026-06-15). The commonsku-style
// commission setup:
//   • Rates  — named commission tiers (label · % · margin or revenue)
//   • People — assign each rep a rate per role on an order (client-rep-only,
//              order-rep-only, both)
//
// GET/POST/PATCH /commissions/rates + GET/PATCH /commissions/people.
// admin / finance only.
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Plus } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { PageHeader } from "@/components/ui/PageHeader";
import { FilterTabs } from "@/components/ui/RecordParts";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

type Basis = "margin" | "revenue";

interface Rate {
  id: string;
  label: string;
  ratePct: number;
  basis: Basis;
  isActive: boolean;
}

interface Person {
  userId: string;
  name: string;
  clientRateId: string | null;
  orderRateId: string | null;
  bothRateId: string | null;
}

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    const b = e.body as { error?: string } | undefined;
    return b?.error ?? `Request failed (${e.status})`;
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

/** Mirrors `CommissionReadiness` in apps/api/src/services/commissions.ts. */
interface CommissionReadinessIssue {
  code: "NO_RATE_ASSIGNED" | "RATE_MISSING" | "RATE_IS_ZERO" | "RATE_MALFORMED";
  /** False for an intentional 0% rate — reported, never nagged about. */
  blocking: boolean;
  repId: string;
  repName: string | null;
  scenario: "client" | "order" | "both";
  projectCount: number;
  message: string;
}

interface CommissionReadiness {
  ready: boolean;
  repsOnProjects: number;
  issues: CommissionReadinessIssue[];
}

const RATES_KEY = ["commissions", "rates"];
const PEOPLE_KEY = ["commissions", "people"];
const READINESS_KEY = ["commissions", "readiness"];

export function CommissionSettingsPage() {
  const { user } = useAuth();
  const canManage =
    user?.roles.some((r) => r === "admin" || r === "finance") ?? false;
  const [tab, setTab] = useState<"rates" | "people">("rates");

  if (!canManage) {
    return (
      <div className="mx-auto max-w-3xl">
        <p className="text-sm text-muted">
          Commission settings are available to finance and admin users.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl">
      <Link
        to="/finance/commissions"
        className="inline-flex min-h-[32px] items-center gap-1 text-[11px] font-medium uppercase tracking-[0.12em] text-muted hover:text-ink"
      >
        <ArrowLeft className="h-3 w-3" /> Commissions
      </Link>

      <PageHeader
        eyebrow="Finance / Commissions / Settings"
        title="Commission settings"
        intro="Create the commission rates you pay, then assign each rep a rate by their role on an order. A rep earns their rate on every paid invoice."
      />

      <CommissionReadinessBanner onGoToPeople={() => setTab("people")} />

      <div className="mb-5 border-b border-rule">
        <FilterTabs
          ariaLabel="Commission settings"
          options={[
            { value: "rates" as const, label: "Rates" },
            { value: "people" as const, label: "People" },
          ]}
          value={tab}
          onChange={setTab}
        />
      </div>

      {tab === "rates" ? (
        <RatesTab />
      ) : (
        <PeopleTab onGoToRates={() => setTab("rates")} />
      )}
    </div>
  );
}

// ─── Rates ─────────────────────────────────────────────────────────────

/**
 * 🔴 WHO WOULD EARN NOTHING — shown before the first live invoice, not after.
 *
 * A rep with no commission rate silently earns nothing. Until now the only
 * warning was a `console.warn` on the invoice run, which is both invisible to
 * the person who can fix it and one step too late — by then a real invoice has
 * been raised. This asks the same question up front.
 *
 * It stays SILENT when everything is configured: a banner that is always on
 * screen is a banner nobody reads. It also only counts the reps who are
 * actually on projects, and only the rate each would actually draw.
 */
function CommissionReadinessBanner(props: { onGoToPeople: () => void }) {
  const { data, isLoading, isError } = useQuery<CommissionReadiness>({
    queryKey: READINESS_KEY,
    queryFn: () => api<CommissionReadiness>("/commissions/readiness"),
  });

  // 🔴 A FAILED CHECK IS NOT A CLEAN ONE. Folding `isError` in with `ready`
  // would render a 500 and "everything is configured" identically — as nothing
  // — which is precisely the silence this feature exists to remove. The rest of
  // this page already shows a plain error state on a failed fetch; so does this.
  if (isError) {
    return (
      <div className="mb-6 rounded-md border border-risk/30 bg-risk-bg p-3">
        <p className="text-xs text-risk">
          Couldn&apos;t check commission readiness. Reload to try again — until
          this loads, treat it as unknown rather than clear.
        </p>
      </div>
    );
  }
  // Nothing to say is the common case, and it should look like nothing.
  if (isLoading || !data || data.ready) return null;

  // Only outstanding problems drive the banner. A deliberate 0% rate (an owner,
  // typically) is shown in the People tab, not shouted about here — a banner
  // that never clears is one nobody reads.
  const outstanding = data.issues.filter((i) => i.blocking);
  const people = new Set(outstanding.map((i) => i.repId)).size;
  // ⚠️ RATE_MALFORMED does NOT earn nothing — it earns an invented 5% nobody
  // chose, which is a different (and arguably more urgent) problem. A blanket
  // "would earn nothing" headline would tell the CEO the wrong story, so the
  // headline stays generic and each row carries its own stakes.
  const earnNothing = outstanding.filter((i) => i.code !== "RATE_MALFORMED");
  const invented = outstanding.filter((i) => i.code === "RATE_MALFORMED");

  return (
    <div className="mb-6 rounded-md border border-warn/30 bg-warn-bg p-4">
      <p className="flex items-center gap-2 text-sm font-semibold text-warn">
        <AlertTriangle className="h-4 w-4" />
        {people} {people === 1 ? "person needs" : "people need"} a commission
        rate sorted
      </p>
      <p className="mt-1 text-xs text-ink">
        These people are on live projects. Commission is calculated the moment
        an invoice is raised, so whatever is set now is what gets paid.
        {earnNothing.length > 0 && (
          <>
            {" "}
            <strong className="font-semibold">
              {earnNothing.length === 1 ? "One" : earnNothing.length} would earn
              $0
            </strong>{" "}
            and nothing would flag it until payroll.
          </>
        )}
        {invented.length > 0 && (
          <>
            {" "}
            <strong className="font-semibold">
              {invented.length === 1 ? "One has" : `${invented.length} have`} an
              unreadable rate
            </strong>{" "}
            and would be paid a fallback percentage nobody chose.
          </>
        )}
      </p>
      <ul className="mt-3 space-y-1.5">
        {outstanding.map((i) => (
          <li key={`${i.repId}:${i.scenario}`} className="text-xs text-ink">
            <span className="font-semibold">{i.repName ?? "Unnamed rep"}</span>
            <span className="text-muted">
              {" "}
              — {i.message}
            </span>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={props.onGoToPeople}
        className="mt-3 inline-flex h-10 items-center gap-1 rounded-md border border-rule bg-white px-3.5 text-sm font-medium text-ink hover:bg-canvas"
      >
        Assign rates
      </button>
    </div>
  );
}

function RatesTab() {
  const queryClient = useQueryClient();
  const [banner, setBanner] = useState<string | null>(null);
  const { data, isLoading, isError } = useQuery({
    queryKey: RATES_KEY,
    queryFn: () => api<{ rates: Rate[] }>("/commissions/rates"),
  });
  const rates = data?.rates ?? [];
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: RATES_KEY });

  return (
    <div>
      <p className="mb-3 text-sm text-muted">
        Create a rate for each commission percentage you pay. “Margin” = profit
        (sale minus cost); “Revenue” = the full sale.
      </p>
      {banner && (
        <div className="mb-4 rounded-md border border-ok/30 bg-ok-bg px-4 py-2 text-sm text-ok">
          {banner}
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-rule bg-white" role="region" tabIndex={0} aria-label="Commission table"><div className="min-w-[560px]">
        <div className="grid grid-cols-[1fr_120px_160px_90px] gap-3 border-b border-[#e7e7e4] bg-[#fafaf8] px-4 py-2.5 text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          <span>Label</span>
          <span className="text-right">Rate</span>
          <span>Basis</span>
          <span className="text-right">Active</span>
        </div>
        {isLoading ? (
          <p className="px-4 py-6 text-sm text-muted">Loading…</p>
        ) : isError ? (
          <p className="px-4 py-6 text-sm text-risk">We couldn&apos;t load rates. Nothing has changed — refresh to try again.</p>
        ) : rates.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted">
            No rates yet — add your first one below.
          </p>
        ) : (
          <div className="divide-y divide-[#e7e7e4]">
            {rates.map((r) => (
              <RateRow
                key={r.id}
                rate={r}
                onSaved={(msg) => {
                  setBanner(msg);
                  invalidate();
                }}
              />
            ))}
          </div>
        )}
      </div></div>

      <AddRateForm
        onAdded={() => {
          setBanner("Rate added.");
          invalidate();
        }}
      />
    </div>
  );
}

function RateRow({
  rate,
  onSaved,
}: {
  rate: Rate;
  onSaved: (msg: string) => void;
}) {
  const [label, setLabel] = useState(rate.label);
  const [pct, setPct] = useState(String(rate.ratePct));
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => setLabel(rate.label), [rate.label]);
  useEffect(() => setPct(String(rate.ratePct)), [rate.ratePct]);

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/commissions/rates/${rate.id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      setErr(null);
      onSaved("Rate updated.");
    },
    onError: (e) => setErr(errText(e)),
  });

  const commitLabel = () => {
    const v = label.trim();
    if (v && v !== rate.label) patch.mutate({ label: v });
  };
  const commitPct = () => {
    const n = Number(pct);
    if (Number.isFinite(n) && n >= 0 && n !== rate.ratePct) patch.mutate({ rate_pct: n });
    else setPct(String(rate.ratePct));
  };

  return (
    <div
      className={cn(
        "grid grid-cols-[1fr_120px_160px_90px] items-center gap-3 px-4 py-2.5",
        !rate.isActive && "opacity-50",
      )}
    >
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onBlur={commitLabel}
        aria-label="Rate label"
        className="h-10 rounded border border-rule bg-white px-2.5 text-sm text-ink"
      />
      <div className="flex items-center justify-end gap-1">
        <input
          inputMode="decimal"
          value={pct}
          onChange={(e) => setPct(e.target.value)}
          onBlur={commitPct}
          aria-label="Rate percent"
          className="h-10 w-16 rounded border border-rule bg-white px-2 text-right text-sm tabular-nums text-ink"
        />
        <span className="text-sm text-muted">%</span>
      </div>
      <select
        value={rate.basis}
        onChange={(e) => patch.mutate({ basis: e.target.value })}
        aria-label="Rate basis"
        className="h-10 rounded border border-rule bg-white px-2 text-sm text-ink"
      >
        <option value="margin">% of margin</option>
        <option value="revenue">% of revenue</option>
      </select>
      <div className="text-right">
        <button
          type="button"
          onClick={() => patch.mutate({ is_active: !rate.isActive })}
          className="min-h-[40px] px-1 text-sm text-muted underline-offset-4 hover:text-ink hover:underline"
        >
          {rate.isActive ? "Archive" : "Restore"}
        </button>
      </div>
      {err && <p className="col-span-4 text-xs text-risk">{err}</p>}
    </div>
  );
}

function AddRateForm({ onAdded }: { onAdded: () => void }) {
  const [label, setLabel] = useState("");
  const [pct, setPct] = useState("");
  const [basis, setBasis] = useState<Basis>("margin");
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api("/commissions/rates", {
        method: "POST",
        body: JSON.stringify({
          label: label.trim(),
          rate_pct: Number(pct),
          basis,
        }),
      }),
    onSuccess: () => {
      setLabel("");
      setPct("");
      setBasis("margin");
      setErr(null);
      onAdded();
    },
    onError: (e) => setErr(errText(e)),
  });

  const valid = label.trim().length > 0 && Number.isFinite(Number(pct)) && Number(pct) >= 0;

  return (
    <div className="mt-3 overflow-x-auto rounded-md border border-dashed border-rule bg-white px-4 py-3">
      <p className="mb-2 text-xs text-muted">Add a rate</p>
      <div className="grid min-w-[528px] grid-cols-[1fr_120px_160px_90px] items-center gap-3">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Account Manager"
          aria-label="New rate label"
          className="h-10 rounded border border-rule bg-white px-2.5 text-sm text-ink"
        />
        <div className="flex items-center justify-end gap-1">
          <input
            inputMode="decimal"
            value={pct}
            onChange={(e) => setPct(e.target.value)}
            placeholder="0"
            className="h-10 w-16 rounded border border-rule bg-white px-2 text-right text-sm tabular-nums text-ink"
          />
          <span className="text-sm text-muted">%</span>
        </div>
        <select
          value={basis}
          onChange={(e) => setBasis(e.target.value as Basis)}
          className="h-10 rounded border border-rule bg-white px-2 text-sm text-ink"
        >
          <option value="margin">% of margin</option>
          <option value="revenue">% of revenue</option>
        </select>
        <div className="text-right">
          <Button
            type="button"
            disabled={!valid || create.isPending}
            onClick={() => create.mutate()}
          >
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>
      </div>
      {err && <p className="mt-2 text-xs text-risk">{err}</p>}
    </div>
  );
}

// ─── People ────────────────────────────────────────────────────────────

function PeopleTab({ onGoToRates }: { onGoToRates: () => void }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: PEOPLE_KEY,
    queryFn: () =>
      api<{ people: Person[]; rates: Rate[] }>("/commissions/people"),
  });
  const people = data?.people ?? [];
  const rates = (data?.rates ?? []).filter((r) => r.isActive);

  // FIRST-USE TRAP (fixed 2026-08-08). With no rates created yet — which is
  // every account's starting state — this tab used to render every rep with
  // three dropdowns containing only "—" and no explanation. It read as broken
  // rather than as "do this first", on the exact screen that decides whether
  // anyone gets paid. Send the admin to the Rates tab instead.
  if (!isLoading && !isError && rates.length === 0) {
    return (
      <div className="rounded-md border border-rule bg-white px-5 py-8 text-center">
        <p className="text-sm font-semibold text-ink">
          No commission rates yet
        </p>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted">
          Create the rates you pay first — then come back here to assign one to
          each rep. A rep with no rate assigned earns no commission on their
          orders.
        </p>
        <Button type="button" onClick={onGoToRates} className="mt-4">
          <Plus className="h-4 w-4" /> Create a rate
        </Button>
      </div>
    );
  }

  return (
    <div>
      <p className="mb-3 text-sm text-muted">
        Set each rep’s rate by their role on an order. If two people work a
        deal, the client rep earns their <em>client-rep-only</em> rate and the
        order rep earns their <em>order-rep-only</em> rate; one person doing
        both earns their <em>both</em> rate.{" "}
        <span className="font-semibold text-ink">
          A rep left unassigned earns nothing on their orders.
        </span>
      </p>
      <div className="overflow-x-auto rounded-md border border-rule bg-white" role="region" tabIndex={0} aria-label="Commission table"><div className="min-w-[560px]">
        <div className="grid grid-cols-[1fr_1fr_1fr_1fr] gap-3 border-b border-[#e7e7e4] bg-[#fafaf8] px-4 py-2.5 text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          <span>Rep</span>
          <span>Client rep only</span>
          <span>Order rep only</span>
          <span>Both</span>
        </div>
        {isLoading ? (
          <p className="px-4 py-6 text-sm text-muted">Loading…</p>
        ) : isError ? (
          <p className="px-4 py-6 text-sm text-risk">We couldn&apos;t load people. Nothing has changed — refresh to try again.</p>
        ) : people.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted">No staff users.</p>
        ) : (
          <div className="divide-y divide-[#e7e7e4]">
            {people.map((p) => (
              <PersonRow key={p.userId} person={p} rates={rates} />
            ))}
          </div>
        )}
      </div></div>
    </div>
  );
}

function PersonRow({ person, rates }: { person: Person; rates: Rate[] }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const patch = useMutation({
    mutationFn: (body: Record<string, string | null>) =>
      api(`/commissions/people/${person.userId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    // 🔴 THE LOOP HAS TO CLOSE. The readiness banner tells an admin to come
    // here and assign a rate; if assigning one leaves the banner still saying
    // "2 people need a rate sorted", the banner has taught them not to believe
    // it. Invalidating READINESS_KEY is what makes the warning disappear on the
    // one action that fixes it.
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: PEOPLE_KEY });
      void qc.invalidateQueries({ queryKey: READINESS_KEY });
    },
    // A silent PATCH failure here means the admin believes they fixed it and
    // did not — the same silent-zero shape, one level up.
    onError: (e) => setError(errText(e)),
  });

  const opt = (selected: string | null, onPick: (v: string | null) => void) => (
    <select
      value={selected ?? ""}
      onChange={(e) => onPick(e.target.value || null)}
      disabled={patch.isPending}
      className="w-full h-10 rounded border border-rule bg-white px-2 text-sm text-ink"
    >
      <option value="">—</option>
      {rates.map((r) => (
        <option key={r.id} value={r.id}>
          {r.label} ({r.ratePct}% {r.basis})
        </option>
      ))}
    </select>
  );

  return (
    <div className="grid grid-cols-[1fr_1fr_1fr_1fr] items-center gap-3 px-4 py-2.5">
      <span className="text-sm font-semibold text-ink">
        {person.name}
        {error && (
          <span className="mt-0.5 block text-xs font-normal text-risk">
            {error}
          </span>
        )}
      </span>
      {opt(person.clientRateId, (v) => patch.mutate({ client_rate_id: v }))}
      {opt(person.orderRateId, (v) => patch.mutate({ order_rate_id: v }))}
      {opt(person.bothRateId, (v) => patch.mutate({ both_rate_id: v }))}
    </div>
  );
}
